import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true
  };
};

// Profile APIs
export const getApplicantProfile = () => axios.get(`${API}/profile/applicant`, getAuthConfig());
export const updateApplicantProfile = (data) => axios.put(`${API}/profile/applicant`, data, getAuthConfig());
export const getEmployerProfile = () => axios.get(`${API}/profile/employer`, getAuthConfig());
export const updateEmployerProfile = (data) => axios.put(`${API}/profile/employer`, data, getAuthConfig());

// Job APIs
export const getJobs = (params) => axios.get(`${API}/jobs`, { ...getAuthConfig(), params });
export const getJob = (jobId) => axios.get(`${API}/jobs/${jobId}`, getAuthConfig());
export const createJob = (data) => axios.post(`${API}/jobs`, data, getAuthConfig());
export const updateJob = (jobId, data) => axios.put(`${API}/jobs/${jobId}`, data, getAuthConfig());
export const deleteJob = (jobId) => axios.delete(`${API}/jobs/${jobId}`, getAuthConfig());
export const getEmployerJobs = () => axios.get(`${API}/employer/jobs`, getAuthConfig());

// Application APIs
export const createApplication = (data) => axios.post(`${API}/applications`, data, getAuthConfig());
export const getMyApplications = () => axios.get(`${API}/applications`, getAuthConfig());
export const getEmployerApplications = (jobId) => axios.get(`${API}/employer/applications`, { ...getAuthConfig(), params: { job_id: jobId } });
export const updateApplicationStatus = (applicationId, status) => axios.put(`${API}/applications/${applicationId}/status?status=${status}`, {}, getAuthConfig());

// Chat APIs
export const createChat = (participantId, jobId) => axios.post(`${API}/chats?participant_id=${participantId}${jobId ? `&job_id=${jobId}` : ''}`, {}, getAuthConfig());
export const getChats = () => axios.get(`${API}/chats`, getAuthConfig());
export const getMessages = (chatId) => axios.get(`${API}/messages/${chatId}`, getAuthConfig());
export const sendMessage = (data) => axios.post(`${API}/messages`, data, getAuthConfig());

// AI APIs
export const generateCV = (language = 'de') => axios.post(`${API}/ai/generate-cv`, { language }, getAuthConfig());
export const generateCoverLetter = (jobId, language = 'de') => axios.post(`${API}/ai/generate-cover-letter`, { job_id: jobId, language }, getAuthConfig());
export const generateJobDescription = (data) => axios.post(`${API}/ai/generate-job`, data, getAuthConfig());
export const translateText = (text, sourceLang, targetLang) => axios.post(`${API}/ai/translate`, { text, source_language: sourceLang, target_language: targetLang }, getAuthConfig());

// Matching APIs
export const getMatchingJobs = () => axios.get(`${API}/matching/jobs`, getAuthConfig());
export const getMatchingCandidates = (jobId) => axios.get(`${API}/matching/candidates`, { ...getAuthConfig(), params: { job_id: jobId } });

// Stats APIs
export const getDashboardStats = () => axios.get(`${API}/stats/dashboard`, getAuthConfig());

// Document APIs
export const downloadCVPDF = () => {
  const token = localStorage.getItem('token');
  return `${API}/documents/cv-pdf?token=${token}`;
};

// Rescue APIs
export const getAusbildungsberufe = () => axios.get(`${API}/rescue/berufe`, getAuthConfig());
export const registerRescueCase = (data) => axios.post(`${API}/rescue/register-case`, data, getAuthConfig());
export const getMyRescueCase = () => axios.get(`${API}/rescue/my-case`, getAuthConfig());
export const getRescueMatches = () => axios.get(`${API}/rescue/match`, getAuthConfig());
export const getRescueStats = () => axios.get(`${API}/rescue/stats`, getAuthConfig());

// Transfer APIs
export const getBranchenTransfer = () => axios.get(`${API}/transfer/branchen`, getAuthConfig());
export const getTransferMatches = () => axios.get(`${API}/transfer/match`, getAuthConfig());

// Skill Heatmap APIs
export const getSkillHeatmap = (jobId) => axios.get(`${API}/heatmap/skills`, { ...getAuthConfig(), params: { job_id: jobId } });

// Anerkennung (Recognition) APIs
export const getAnerkennungClusters = () => axios.get(`${API}/anerkennung/clusters`, getAuthConfig());
export const registerAnerkennung = (data) => axios.post(`${API}/anerkennung/register`, data, getAuthConfig());
export const getMyAnerkennung = () => axios.get(`${API}/anerkennung/my-case`, getAuthConfig());
export const updateAnerkennungStep = (step, documents) => axios.put(`${API}/anerkennung/update-step?step=${step}`, {}, { ...getAuthConfig(), params: { documents } });

// Blocked Jobs APIs
export const blockJob = (jobId) => axios.post(`${API}/blocked-jobs/${jobId}`, {}, getAuthConfig());
export const unblockJob = (jobId) => axios.delete(`${API}/blocked-jobs/${jobId}`, getAuthConfig());
export const getBlockedJobs = () => axios.get(`${API}/blocked-jobs`, getAuthConfig());

// Telekom Pilot APIs
export const getTelekomConfig = () => axios.get(`${API}/telekom-pilot/config`, getAuthConfig());
export const addTelekomEmployee = (data) => axios.post(`${API}/telekom-pilot/employees`, data, getAuthConfig());
export const getTelekomEmployees = () => axios.get(`${API}/telekom-pilot/employees`, getAuthConfig());
export const getTelekomKPIs = () => axios.get(`${API}/telekom-pilot/kpis`, getAuthConfig());
export const updateEmployeeStatus = (id, status, matchScore) => axios.put(`${API}/telekom-pilot/employees/${id}/status?status=${status}&match_score=${matchScore || 0}`, {}, getAuthConfig());

// Rescue Partner (Employer) APIs
export const getRescuePartner = () => axios.get(`${API}/employer/rescue-partner`, getAuthConfig());
export const updateRescuePartner = (data) => axios.put(`${API}/employer/rescue-partner`, data, getAuthConfig());

// Redeployment APIs
export const getRedeploymentReasons = () => axios.get(`${API}/redeployment/reasons/list`, getAuthConfig());
export const createRedeployment = (data) => axios.post(`${API}/redeployment/create`, data, getAuthConfig());
export const getRedeploymentPlans = () => axios.get(`${API}/redeployment/my-plans`, getAuthConfig());
export const getRedeploymentPlan = (planId) => axios.get(`${API}/redeployment/${planId}`, getAuthConfig());

// Senior / 55+ APIs
export const getSeniorModels = () => axios.get(`${API}/senior/models`, getAuthConfig());
export const registerSeniorProfile = (data) => axios.post(`${API}/senior/register`, data, getAuthConfig());
export const getMySeniorProfile = () => axios.get(`${API}/senior/my-profile`, getAuthConfig());
export const getSeniorOpportunities = () => axios.get(`${API}/senior/opportunities`, getAuthConfig());

// Subscription APIs
export const getSubscriptionPlans = () => axios.get(`${API}/subscriptions/plans`, getAuthConfig());
export const getMySubscription = () => axios.get(`${API}/subscriptions/my-plan`, getAuthConfig());
export const upgradeSubscription = (planId) => axios.post(`${API}/subscriptions/upgrade`, { plan_id: planId }, getAuthConfig());

export default {
  getApplicantProfile,
  updateApplicantProfile,
  getEmployerProfile,
  updateEmployerProfile,
  getJobs,
  getJob,
  createJob,
  updateJob,
  deleteJob,
  getEmployerJobs,
  createApplication,
  getMyApplications,
  getEmployerApplications,
  updateApplicationStatus,
  createChat,
  getChats,
  getMessages,
  sendMessage,
  generateCV,
  generateCoverLetter,
  generateJobDescription,
  translateText,
  getMatchingJobs,
  getMatchingCandidates,
  getDashboardStats,
  downloadCVPDF,
  getAusbildungsberufe,
  registerRescueCase,
  getMyRescueCase,
  getRescueMatches,
  getRescueStats,
  getBranchenTransfer,
  getTransferMatches,
  getSkillHeatmap,
  getAnerkennungClusters,
  registerAnerkennung,
  getMyAnerkennung,
  updateAnerkennungStep
};
